'use client';
import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/molecules/Modal/Modal';
import { useModalController } from '@/components/molecules/Modal/useModalController';
import { Button, MultiSelect, MultiSelectOption } from '@/components/atoms';
import { Widget } from '@/modules/widgets/types';
import { shareEndpoints, UserSearchResult } from '@/modules/widgets/api/shareEndpoints';
import { useUser } from '@/modules/auth/context/UserContext';

interface ShareWidgetModalProps {
    widget: Widget;
    controller: ReturnType<typeof useModalController>;
}

export const ShareWidgetModal = ({ widget, controller }: ShareWidgetModalProps) => {
    const { user } = useUser();
    const [options, setOptions] = useState<MultiSelectOption[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isSearching, setIsSearching] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasTyped, setHasTyped] = useState(false);

    // Stable refs — survive across re-renders and search queries
    const guidToId = useRef(new Map<string, number>());
    const idToUser = useRef(new Map<number, UserSearchResult>());
    const idToOption = useRef(new Map<number, MultiSelectOption>());
    const selectedIdsRef = useRef<number[]>([]);
    const nextId = useRef(0);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const ensureId = (u: UserSearchResult): number => {
        if (!guidToId.current.has(u.userGuid)) {
            const id = ++nextId.current;
            guidToId.current.set(u.userGuid, id);
            idToUser.current.set(id, u);
        }
        return guidToId.current.get(u.userGuid)!;
    };

    const makeOption = (u: UserSearchResult): MultiSelectOption => {
        const id = ensureId(u);
        const opt: MultiSelectOption = {
            id,
            label: u.email,
            category: u.name || undefined,
        };
        idToOption.current.set(id, opt);
        return opt;
    };

    // Build options = search results + any currently-selected users not in results
    const applyResults = (searchOpts: MultiSelectOption[]) => {
        const searchIds = new Set(searchOpts.map((o) => o.id));
        const persistedSelected = selectedIdsRef.current
            .map((id) => idToOption.current.get(id))
            .filter((o): o is MultiSelectOption => o !== undefined && !searchIds.has(o.id));
        setOptions([...searchOpts, ...persistedSelected]);
    };

    const fetchAndApply = async (term: string) => {
        setIsSearching(true);
        try {
            const users = await shareEndpoints.searchUsers(term);
            applyResults(users.map(makeOption));
        } finally {
            setIsSearching(false);
        }
    };

    const handleSearch = (term: string) => {
        if (term.trim()) setHasTyped(true);
        // Show the loading state immediately so the dropdown reads "Loading…" during
        // the debounce + request window instead of momentarily showing an empty/no-results state.
        setIsSearching(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchAndApply(term), 300);
    };

    const handleChange = (ids: number[]) => {
        setSelectedIds(ids);
        selectedIdsRef.current = ids;
    };

    // Load existing shares when modal opens; reset state on close
    useEffect(() => {
        if (!controller.isOpen) {
            guidToId.current.clear();
            idToUser.current.clear();
            idToOption.current.clear();
            selectedIdsRef.current = [];
            nextId.current = 0;
            setOptions([]);
            setSelectedIds([]);
            setIsSearching(true);
            setHasTyped(false);
            return;
        }

        Promise.all([
            shareEndpoints.searchUsers(''),
            shareEndpoints.getWidgetShares(widget.id),
        ]).then(([allUsers, shares]) => {
            const otherUsers = allUsers.filter(
                (u) => u.userGuid !== user?.userId && u.email.toLowerCase() !== user?.userName?.toLowerCase(),
            );
            const userByGuid = new Map(otherUsers.map((u) => [u.userGuid, u]));
            const userByEmail = new Map(otherUsers.map((u) => [u.email.toLowerCase(), u]));

            // Resolve shares first so selected IDs are set before applyResults runs
            const initialOpts = shares.map((s) => {
                const resolved =
                    userByGuid.get(s.userId) ??
                    userByEmail.get((s.sharedToEmail ?? '').toLowerCase()) ?? {
                        userGuid: s.userId,
                        email: s.sharedToEmail ?? s.userId,
                        name: '',
                    };
                return makeOption(resolved);
            });
            const initialIds = initialOpts.map((o) => o.id);
            selectedIdsRef.current = initialIds;
            setSelectedIds(initialIds);

            // Now apply results — applyResults will append selected users that
            // aren't in the first-50 list so their chips always render
            applyResults(otherUsers.map(makeOption));
            setIsSearching(false);
        });
    }, [controller.isOpen, widget.id]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await shareEndpoints.syncWidgetShares({
                widgetId: widget.id,
                shares: selectedIdsRef.current
                    .map((id) => idToUser.current.get(id))
                    .filter((u): u is UserSearchResult => u !== undefined)
                    .map((u) => ({
                        userId: u.userGuid,
                        sharedByEmail: user?.userName ?? null,
                        sharedToEmail: u.email,
                    })),
            });
            controller.close();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            title="Share widget"
            controller={controller}
            closeOnOverlayClick
            closeOnEsc
            bodyClassName="overflow-visible max-w-lg"
            actionButtons={
                <>
                    <Button variant="secondary" onClick={controller.close} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} isLoading={isSaving}>
                        Save
                    </Button>
                </>
            }
        >
            <MultiSelect
                label="Share with"
                options={options}
                selectedIds={selectedIds}
                onChange={handleChange}
                onSearch={handleSearch}
                placeholder="Search by name or email…"
                emptyMessage={hasTyped ? "No users found" : "Type to search for users…"}
                isLoading={isSearching}
                searchable
                showSelectAll={false}
            />
        </Modal>
    );
};
